import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ENDPOINT_URL from "../../config";
import NavbarReviewer from "../Reviewer/ReviewerUI/NavbarReviewer";
import SidebarReviewer from "../Reviewer/ReviewerUI/SidebarReviewer";
import "./ReviewerApplications.css";
import { toast } from "react-toastify";

const ReviewerApplications = () => {
  const navigate = useNavigate();
  const { pipelineId } = useParams();
  const [reviewer, setReviewer] = useState({});
  const [applications, setApplications] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
// START CHANGE: ANC-style global selection (multi-page persistent)
const [selectedIds, setSelectedIds] = useState(new Set());
const headerCbRef = React.useRef(null);
// END CHANGE

  // ---------------- FETCH REVIEWER DETAILS ----------------
  const fetchReviewerDetails = async () => {
    try {
      const res = await fetch(`${ENDPOINT_URL}/api/reviewers/me`, {
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || "Failed to load reviewer details");
      }

      const data = await res.json();
      setReviewer(data);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ---------------- FETCH ASSIGNED APPLICATIONS ----------------
  const fetchAssignedForms = async () => {
    try {
      const token = localStorage.getItem("reviewer_token");

      const res = await fetch(`${ENDPOINT_URL}/api/reviewer/forms`, {
        method: "GET",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || "Unable to load applications");
      }

      const data = await res.json();
      const allForms = Array.isArray(data) ? data : data.forms || [];

      const filtered = pipelineId
        ? allForms.filter((f) => f.pipelineId === pipelineId)
        : allForms;

      setApplications(filtered);
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    fetchReviewerDetails();
    fetchAssignedForms();
  }, [pipelineId]);

  // ---------------- DATE & SORT LOGIC ----------------
  const getSubmissionDate = (app) => {
    const d = new Date(app.formSubmissionTime || app.createdAt);
    return isNaN(d) ? null : d;
  };

  const filterByDate = (app) => {
    const d = getSubmissionDate(app);
    if (!d) return false;

    let ok = true;
    if (startDate) ok = ok && d >= new Date(startDate);
    if (endDate) ok = ok && d <= new Date(endDate);
    return ok;
  };
  

  // ===============================
// START: SEARCH + DEBOUNCE + HIGHLIGHT LOGIC (ANC replica)
// ===============================

// Debounce utility
const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

// Raw text from search input
const [searchQuery, setSearchQuery] = useState("");

// Debounced search text
const [debouncedSearch, setDebouncedSearch] = useState("");

useEffect(() => {
  const handler = debounce((q) => setDebouncedSearch(q), 300);
  handler(searchQuery);
}, [searchQuery]);

// Highlight logic (same as old ANC project)
const highlightMatch = (text, keyword) => {
  if (!keyword) return text;
  const regex = new RegExp(`(${keyword})`, "ig");
  return text.replace(regex, "<mark>$1</mark>");
};

// Filter by search (applicant name or email)
const applySearch = (app) => {
  if (!debouncedSearch.trim()) return true;

  const q = debouncedSearch.toLowerCase();
  const name = (app.formData?.["Name"] || "").toLowerCase();
  const email = (app.formData?.["Email"] || "").toLowerCase();

  return name.includes(q) || email.includes(q);
};

// ===============================
// END: SEARCH + DEBOUNCE + HIGHLIGHT LOGIC
// ===============================

  const sortByDate = (a, b) => {
    const da = getSubmissionDate(a);
    const db = getSubmissionDate(b);
    if (!da || !db) return 0;
    return sortOrder === "asc" ? da - db : db - da;
  };


// ===============================
// START: Updated processedApplications (added search filter)
// ===============================
const processedApplications = [...applications]
  .filter(filterByDate)
  .filter(applySearch)   // <-- NEW LINE
  .sort(sortByDate);
// ===============================
// END: Updated processedApplications
// ===============================

    
/* ===========================
   START PAGINATION STATE (ANC Replica)
   =========================== */
const [itemsPerPage, setItemsPerPage] = useState(10);
const [currentPage, setCurrentPage] = useState(1);

const totalPages = Math.ceil(processedApplications.length / itemsPerPage);

const startIndex = (currentPage - 1) * itemsPerPage;
const endIndex = startIndex + itemsPerPage;

const paginatedApplications = processedApplications.slice(startIndex, endIndex);
/* ===========================
   END PAGINATION STATE
   =========================== */
/* ===========================
   START HANDLE PAGE CHANGE (ANC Replica)
   =========================== */
const handlePageChange = (page) => {
  if (page >= 1 && page <= totalPages) {
    setCurrentPage(page);
  }
};
/* ===========================
   END HANDLE PAGE CHANGE
   =========================== */
//  START CHANGE: Update header checkbox indeterminate state
useEffect(() => {
  if (!headerCbRef.current) return;

  const total = processedApplications.length;
  const selectedCount = processedApplications.filter((app) =>
    selectedIds.has(app._id)
  ).length;

  headerCbRef.current.indeterminate =
    selectedCount > 0 && selectedCount < total;
}, [processedApplications, selectedIds]);
//  END CHANGE

  return (
    <>
      <NavbarReviewer />
      <SidebarReviewer />

      <div className="reviewer-applications-wrapper-12ab-reviewerapplications">

        {/* ---------------- FILTER BAR ---------------- */}
        <div className="reviewer-filter-bar-12ab-reviewerapplications">

          <div className="filter-item-12ab-reviewerapplications">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-item-12ab-reviewerapplications">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="filter-item-12ab-reviewerapplications">
            <label>Sort</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

{/* ================= START: Search + Total in One Row ================= */}
<div className="search-total-row-12ab">

  {/* Search Field */}
  <div className="filter-item-12ab-reviewerapplications search-right-12ab">
    <label>Search</label>

    <div className="search-wrapper-12ab-reviewerapplications">
      <input
        type="text"
        placeholder="Search applicant by name, email"
        title="Search applicant by name, email"
        className="input-search-12ab-reviewerapplications"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {searchQuery && (
        <button
          type="button"
          title="Clear search"
          className="clear-search-btn-12ab-reviewerapplications"
          onClick={() => setSearchQuery("")}
        >
          ×
        </button>
      )}
    </div>
  </div>

  {/* Total Count */}
  <div className="total-count-box-12ab-reviewerapplications">
    <div className="total-icon-12ab-reviewerapplications">
      <img
        src="/navbar/total-patient.png"
        alt="total"
        className="total-icon-img-12ab-reviewerapplications" 
      />
    </div>

    <p className="total-label-12ab-reviewerapplications">
      Total Applicant: <span className="total-number-12ab-reviewerapplications">
        {processedApplications.length}
      </span>
    </p>
  </div>

</div>
{/* ================= END: Search + Total ================= */}

        </div>


        {/* ---------------- TABLE VIEW ---------------- */}
<table className="reviewer-table-anc-12ab-reviewerapplications">
  <thead>
    <tr>
      <th>
        {/* <input
          type="checkbox"
          onChange={() => {}}
          className="reviewer-checkbox-12ab-reviewerapplications"
        /> */}
        {/* START CHANGE: Header checkbox selects ALL filtered applications */}
<input
  type="checkbox"
  ref={headerCbRef}
  checked={
    processedApplications.length > 0 &&
    processedApplications.every((app) => selectedIds.has(app._id))
  }
  onChange={(e) => {
    const next = new Set(selectedIds);
    const isChecked = e.target.checked;

    processedApplications.forEach((app) => {
      if (isChecked) next.add(app._id);
      else next.delete(app._id);
    });

    setSelectedIds(next);
  }}
  className="reviewer-checkbox-12ab-reviewerapplications"
/>
{/*  END CHANGE */}

      </th>
      <th>S.No</th>
      <th>Applicant</th>
      <th>Email</th>
      <th>Submitted On</th>
      <th>Action</th>
    </tr>
  </thead>

  <tbody>
    {/* START: NO RESULTS FOUND */}
{processedApplications.length === 0 && (
  <tr>
    <td colSpan="6" style={{ textAlign: "center", padding: "18px", color: "#666" }}>
      No results found
    </td>
  </tr>
)}
{/* END: NO RESULTS FOUND */}

    {/* {processedApplications.map((app, index) => { */}
    {paginatedApplications.map((app, index) => {
      const applicantName = app.formData?.["Name"] || "Unknown";
      const applicantEmail = app.formData?.["Email"] || "N/A";

      const date = getSubmissionDate(app);
      const formatted = date ? date.toLocaleDateString("en-GB") : "N/A";

      const formId = app.formId || app._id;

      return (
        <tr key={formId}>
          {/* Checkbox */}
          <td>
            {/* <input
              type="checkbox"
              className="reviewer-checkbox-12ab-reviewerapplications"
            /> */}
            {/* START CHANGE: Row checkbox (multi-page persistent) */}
<input
  type="checkbox"
  checked={selectedIds.has(formId)}
  onChange={(e) => {
    const next = new Set(selectedIds);
    if (e.target.checked) next.add(formId);
    else next.delete(formId);
    setSelectedIds(next);
  }}
  className="reviewer-checkbox-12ab-reviewerapplications"
/>
{/*  END CHANGE */}

          </td>

          {/* S.No */}
          <td>{index + 1}</td>

          {/* Applicant */}
          {/* <td>{applicantName}</td> */}
          {/* START highlight applicant */}
<td
  dangerouslySetInnerHTML={{
    __html: highlightMatch(applicantName, debouncedSearch),
  }}
/>
{/* END highlight applicant */}
          {/* Email */}
          {/* <td className="reviewer-email-cell-12ab-reviewerapplications">
            {applicantEmail}
          </td> */}
{/* START highlight email */}
<td
  className="reviewer-email-cell-12ab-reviewerapplications"
  dangerouslySetInnerHTML={{
    __html: highlightMatch(applicantEmail, debouncedSearch),
  }}
/>
{/* END highlight email */}

          {/* Submitted On */}
          <td>{formatted}</td>
          {/* Action */}
<td>
  {/* <span
    className="reviewer-action-link-12ab-reviewerapplications"
    onClick={() => {
      // Extract file same as OLD logic
      const fileObj = app.files?.find((f) =>
        f.labelName?.some((lbl) =>
          lbl.toLowerCase().includes("upload the presentation")
        )
      );

      navigate(`/reviewer/evaluate/${app._id}`, {
        state: {
          applicantName: app.formData?.["Name"] || "",
          applicantEmail: app.formData?.["Email"] || "",
          applicantConceptFile: fileObj?.path || "",
          app, // optional: send whole app if needed
        },
      });
    }}
  >
    Review Application
  </span> */}
  <span
  className="reviewer-action-link-12ab-reviewerapplications"
  onClick={() => {
    
    // NEW LOGIC → extract first file
    const fileObj = app.files?.[0];

    navigate(`/reviewer/evaluate/${app._id}`, {
      state: {
        applicantName: app.formData?.["Name"] || "",
        applicantEmail: app.formData?.["Email"] || "",
        applicantConceptFile: fileObj?.path || "",
        app, 
      },
    });
  }}
>
  {/* Review Application */}
  {/* Review */}
  Evaluate
</span>

</td>


        </tr>
      );
    })}
  </tbody>
</table>

{/* ---------------- CARD VIEW (Mobile) ---------------- */}
<div className="reviewer-card-container-12ab-reviewerapplications">
  {/* {processedApplications.map((app) => { */}
  {paginatedApplications.map((app) => {
    const applicantName = app.formData?.["Name"] || "Unknown";
    const applicantEmail = app.formData?.["Email"] || "N/A";
    const formId = app.formId || app._id;

    const date = getSubmissionDate(app);
    const formatted = date ? date.toLocaleDateString("en-GB") : "N/A";

    return (
     <div
  key={formId}
  className="reviewer-card-12ab-reviewerapplications"
>
  <div className="reviewer-card-header-12ab">
    <div>
      {/* <p><strong>Applicant:</strong> {applicantName}</p>
      <p><strong>Email:</strong> {applicantEmail}</p> */}
      {/* START highlight applicant (CARD) */}
<p>
  <strong>Applicant:</strong>{" "}
  <span
    dangerouslySetInnerHTML={{
      __html: highlightMatch(applicantName, debouncedSearch),
    }}
  />
</p>
{/* END highlight applicant (CARD) */}
{/* START highlight email (CARD) */}
<p>
  <strong>Email:</strong>{" "}
  <span
    dangerouslySetInnerHTML={{
      __html: highlightMatch(applicantEmail, debouncedSearch),
    }}
  />
</p>
{/* END highlight email (CARD) */}

      <p><strong>Submitted On:</strong> {formatted}</p>
    </div>

<button
  className="reviewer-small-btn-12ab-reviewerapplications"
  onClick={() => {

    // SAME LOGIC AS TABLE: extract first file
    const fileObj = app.files?.[0];

    navigate(`/reviewer/evaluate/${formId}`, {
      state: {
        applicantName,
        applicantEmail,
        applicantConceptFile: fileObj?.path || "",
        app,
      },
    });
  }}
>
  Evaluate
</button>

  </div>
</div>

    );
  })}
</div>


{/* ============================================
    START PAGINATION UI (Exact ANC Replica)
============================================ */}
<div className="pagination-wrapper-12ab-reviewerapplications flex flex-col sm:flex-row sm:justify-between items-start sm:items-center mt-4 mb-6 px-2 w-full gap-2 text-xs">

<div className="page-buttons-12ab-reviewersummary-final">

  {/* Go back 5 pages */}
  {currentPage > 5 && (
    <button
      title="Go back 5 pages"
      onClick={() => setCurrentPage(prev => Math.max(prev - 5, 1))}
      className="page-btn-12ab-reviewersummary-final"
    >
      {"<<"}
    </button>
  )}

  {/* Prev */}
  <button
    {...(currentPage !== 1 && { title: "Previous page" })}
    onClick={() => handlePageChange(currentPage - 1)}
    disabled={currentPage === 1}
    className={`page-btn-12ab-reviewersummary-final ${
      currentPage === 1 ? "disabled-12ab-reviewersummary-final" : ""
    }`}
  >
    {"<"}
  </button>

  {/* Page Numbers */}
  {(() => {
    const groupStart = Math.floor((currentPage - 1) / 5) * 5 + 1;

    return [...Array(totalPages)]
      .map((_, i) => i + 1)
      .filter(page => page >= groupStart && page < groupStart + 5)
      .map(page => (
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          className={`page-btn-12ab-reviewersummary-final ${
            currentPage === page ? "active-page-12ab-reviewersummary-final" : ""
          }`}
        >
          {page}
        </button>
      ));
  })()}

  {/* Next */}
  <button
    {...(currentPage !== totalPages && { title: "Next page" })}
    onClick={() => handlePageChange(currentPage + 1)}
    disabled={currentPage === totalPages}
    className={`page-btn-12ab-reviewersummary-final ${
      currentPage === totalPages ? "disabled-12ab-reviewersummary-final" : ""
    }`}
  >
    {">"}
  </button>

  {/* Go forward 5 pages */}
  {currentPage <= totalPages - 5 && (
    <button
      title="Go forward 5 pages"
      onClick={() => setCurrentPage(prev => Math.min(prev + 5, totalPages))}
      className="page-btn-12ab-reviewersummary-final"
    >
      {">>"}
    </button>
  )}
</div>


  {/* Page range info */}

<div className="page-range-text-12ab-reviewersummary-final">
  {(() => {
    const groupStart = Math.floor((currentPage - 1) / 5) * 5 + 1;
    const groupEnd = Math.min(groupStart + 4, totalPages);
    return `Showing pages ${groupStart}–${groupEnd} of ${totalPages}`;
  })()}
</div>

{/* Items per page dropdown */}
<div className="items-per-page-wrapper-12ab-reviewersummary-final">
  <select
    value={itemsPerPage}
    onChange={(e) => {
      setItemsPerPage(parseInt(e.target.value));
      setCurrentPage(1);
    }}
    className="items-per-page-select-12ab-reviewersummary-final"
  >
    <option value="10">10</option>
    <option value="30">30</option>
    <option value="50">50</option>
    <option value="100">100</option>
  </select>

  <span className="items-per-page-label-12ab-reviewersummary-final">/Page</span>
</div>


</div>
{/* END PAGINATION UI */}


      </div>
    </>
  );
};

export default ReviewerApplications;



/////////before pagination 
// import React, { useEffect, useState } from "react";
// import { Link, useNavigate, useParams } from "react-router-dom";
// import ENDPOINT_URL from "../../config";
// import NavbarReviewer from "../Reviewer/ReviewerUI/NavbarReviewer";
// import SidebarReviewer from "../Reviewer/ReviewerUI/SidebarReviewer";
// import "./ReviewerApplications.css";
// import { toast } from "react-toastify";

// const ReviewerApplications = () => {
//   const navigate = useNavigate();
//   const { pipelineId } = useParams();

//   const [reviewer, setReviewer] = useState({});
//   const [applications, setApplications] = useState([]);

//   const [sortOrder, setSortOrder] = useState("desc");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");

//   // ---------------- FETCH REVIEWER DETAILS ----------------
//   const fetchReviewerDetails = async () => {
//     try {
//       const res = await fetch(`${ENDPOINT_URL}/api/reviewers/me`, {
//         credentials: "include",
//       });

//       if (!res.ok) {
//         const err = await res.json().catch(() => null);
//         throw new Error(err?.error || "Failed to load reviewer details");
//       }

//       const data = await res.json();
//       setReviewer(data);
//     } catch (err) {
//       toast.error(err.message);
//     }
//   };

//   // ---------------- FETCH ASSIGNED APPLICATIONS ----------------
//   const fetchAssignedForms = async () => {
//     try {
//       const token = localStorage.getItem("reviewer_token");

//       const res = await fetch(`${ENDPOINT_URL}/api/reviewer/forms`, {
//         method: "GET",
//         credentials: "include",
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "application/json",
//         },
//       });

//       if (!res.ok) {
//         const err = await res.json().catch(() => null);
//         throw new Error(err?.message || "Unable to load applications");
//       }

//       const data = await res.json();
//       const allForms = Array.isArray(data) ? data : data.forms || [];

//       const filtered = pipelineId
//         ? allForms.filter((f) => f.pipelineId === pipelineId)
//         : allForms;

//       setApplications(filtered);
//     } catch (err) {
//       toast.error(err.message);
//     }
//   };

//   useEffect(() => {
//     fetchReviewerDetails();
//     fetchAssignedForms();
//   }, [pipelineId]);

//   // ---------------- DATE & SORT LOGIC ----------------
//   const getSubmissionDate = (app) => {
//     const d = new Date(app.formSubmissionTime || app.createdAt);
//     return isNaN(d) ? null : d;
//   };

//   const filterByDate = (app) => {
//     const d = getSubmissionDate(app);
//     if (!d) return false;

//     let ok = true;
//     if (startDate) ok = ok && d >= new Date(startDate);
//     if (endDate) ok = ok && d <= new Date(endDate);
//     return ok;
//   };
  

//   // ===============================
// // START: SEARCH + DEBOUNCE + HIGHLIGHT LOGIC (ANC replica)
// // ===============================

// // Debounce utility
// const debounce = (fn, delay) => {
//   let timer;
//   return (...args) => {
//     clearTimeout(timer);
//     timer = setTimeout(() => fn(...args), delay);
//   };
// };

// // Raw text from search input
// const [searchQuery, setSearchQuery] = useState("");

// // Debounced search text
// const [debouncedSearch, setDebouncedSearch] = useState("");

// useEffect(() => {
//   const handler = debounce((q) => setDebouncedSearch(q), 300);
//   handler(searchQuery);
// }, [searchQuery]);

// // Highlight logic (same as old ANC project)
// const highlightMatch = (text, keyword) => {
//   if (!keyword) return text;
//   const regex = new RegExp(`(${keyword})`, "ig");
//   return text.replace(regex, "<mark>$1</mark>");
// };

// // Filter by search (applicant name or email)
// const applySearch = (app) => {
//   if (!debouncedSearch.trim()) return true;

//   const q = debouncedSearch.toLowerCase();
//   const name = (app.formData?.["Name"] || "").toLowerCase();
//   const email = (app.formData?.["Email"] || "").toLowerCase();

//   return name.includes(q) || email.includes(q);
// };

// // ===============================
// // END: SEARCH + DEBOUNCE + HIGHLIGHT LOGIC
// // ===============================

//   const sortByDate = (a, b) => {
//     const da = getSubmissionDate(a);
//     const db = getSubmissionDate(b);
//     if (!da || !db) return 0;
//     return sortOrder === "asc" ? da - db : db - da;
//   };


// // ===============================
// // START: Updated processedApplications (added search filter)
// // ===============================
// const processedApplications = [...applications]
//   .filter(filterByDate)
//   .filter(applySearch)   // <-- NEW LINE
//   .sort(sortByDate);
// // ===============================
// // END: Updated processedApplications
// // ===============================

    
//   return (
//     <>
//       <NavbarReviewer />
//       <SidebarReviewer />

//       <div className="reviewer-applications-wrapper-12ab-reviewerapplications">

//         {/* ---------------- FILTER BAR ---------------- */}
//         <div className="reviewer-filter-bar-12ab-reviewerapplications">

//           <div className="filter-item-12ab-reviewerapplications">
//             <label>Start Date</label>
//             <input
//               type="date"
//               value={startDate}
//               onChange={(e) => setStartDate(e.target.value)}
//             />
//           </div>

//           <div className="filter-item-12ab-reviewerapplications">
//             <label>End Date</label>
//             <input
//               type="date"
//               value={endDate}
//               onChange={(e) => setEndDate(e.target.value)}
//             />
//           </div>

//           <div className="filter-item-12ab-reviewerapplications">
//             <label>Sort</label>
//             <select
//               value={sortOrder}
//               onChange={(e) => setSortOrder(e.target.value)}
//             >
//               <option value="desc">Newest First</option>
//               <option value="asc">Oldest First</option>
//             </select>
//           </div>

// {/* ================= START: Search + Total in One Row ================= */}
// <div className="search-total-row-12ab">

//   {/* Search Field */}
//   <div className="filter-item-12ab-reviewerapplications search-right-12ab">
//     <label>Search</label>

//     <div className="search-wrapper-12ab-reviewerapplications">
//       <input
//         type="text"
//         placeholder="Search applicant by name, email"
//         title="Search applicant by name, email"
//         className="input-search-12ab-reviewerapplications"
//         value={searchQuery}
//         onChange={(e) => setSearchQuery(e.target.value)}
//       />

//       {searchQuery && (
//         <button
//           type="button"
//           title="Clear search"
//           className="clear-search-btn-12ab-reviewerapplications"
//           onClick={() => setSearchQuery("")}
//         >
//           ×
//         </button>
//       )}
//     </div>
//   </div>

//   {/* Total Count */}
//   <div className="total-count-box-12ab-reviewerapplications">
//     <div className="total-icon-12ab-reviewerapplications">
//       <img
//         src="/icons/total-patient.png"
//         alt="total"
//         className="total-icon-img-12ab-reviewerapplications"
//       />
//     </div>

//     <p className="total-label-12ab-reviewerapplications">
//       Total Applicant: <span className="total-number-12ab-reviewerapplications">
//         {processedApplications.length}
//       </span>
//     </p>
//   </div>

// </div>
// {/* ================= END: Search + Total ================= */}

//         </div>


//         {/* ---------------- TABLE VIEW ---------------- */}
// <table className="reviewer-table-anc-12ab-reviewerapplications">
//   <thead>
//     <tr>
//       <th>
//         <input
//           type="checkbox"
//           onChange={() => {}}
//           className="reviewer-checkbox-12ab-reviewerapplications"
//         />
//       </th>
//       <th>S.No</th>
//       <th>Applicant</th>
//       <th>Email</th>
//       <th>Submitted On</th>
//       <th>Action</th>
//     </tr>
//   </thead>

//   <tbody>
//     {/* START: NO RESULTS FOUND */}
// {processedApplications.length === 0 && (
//   <tr>
//     <td colSpan="6" style={{ textAlign: "center", padding: "18px", color: "#666" }}>
//       No results found
//     </td>
//   </tr>
// )}
// {/* END: NO RESULTS FOUND */}

//     {processedApplications.map((app, index) => {
//       const applicantName = app.formData?.["Name"] || "Unknown";
//       const applicantEmail = app.formData?.["Email"] || "N/A";

//       const date = getSubmissionDate(app);
//       const formatted = date ? date.toLocaleDateString("en-GB") : "N/A";

//       const formId = app.formId || app._id;

//       return (
//         <tr key={formId}>
//           {/* Checkbox */}
//           <td>
//             <input
//               type="checkbox"
//               className="reviewer-checkbox-12ab-reviewerapplications"
//             />
//           </td>

//           {/* S.No */}
//           <td>{index + 1}</td>

//           {/* Applicant */}
//           {/* <td>{applicantName}</td> */}
//           {/* START highlight applicant */}
// <td
//   dangerouslySetInnerHTML={{
//     __html: highlightMatch(applicantName, debouncedSearch),
//   }}
// />
// {/* END highlight applicant */}
//           {/* Email */}
//           {/* <td className="reviewer-email-cell-12ab-reviewerapplications">
//             {applicantEmail}
//           </td> */}
// {/* START highlight email */}
// <td
//   className="reviewer-email-cell-12ab-reviewerapplications"
//   dangerouslySetInnerHTML={{
//     __html: highlightMatch(applicantEmail, debouncedSearch),
//   }}
// />
// {/* END highlight email */}

//           {/* Submitted On */}
//           <td>{formatted}</td>
//           {/* Action */}
// <td>
//   {/* <span
//     className="reviewer-action-link-12ab-reviewerapplications"
//     onClick={() => {
//       // Extract file same as OLD logic
//       const fileObj = app.files?.find((f) =>
//         f.labelName?.some((lbl) =>
//           lbl.toLowerCase().includes("upload the presentation")
//         )
//       );

//       navigate(`/reviewer/evaluate/${app._id}`, {
//         state: {
//           applicantName: app.formData?.["Name"] || "",
//           applicantEmail: app.formData?.["Email"] || "",
//           applicantConceptFile: fileObj?.path || "",
//           app, // optional: send whole app if needed
//         },
//       });
//     }}
//   >
//     Review Application
//   </span> */}
//   <span
//   className="reviewer-action-link-12ab-reviewerapplications"
//   onClick={() => {
    
//     // NEW LOGIC → extract first file
//     const fileObj = app.files?.[0];

//     navigate(`/reviewer/evaluate/${app._id}`, {
//       state: {
//         applicantName: app.formData?.["Name"] || "",
//         applicantEmail: app.formData?.["Email"] || "",
//         applicantConceptFile: fileObj?.path || "",
//         app, 
//       },
//     });
//   }}
// >
//   {/* Review Application */}
//   {/* Review */}
//   Evaluate
// </span>

// </td>


//         </tr>
//       );
//     })}
//   </tbody>
// </table>

// {/* ---------------- CARD VIEW (Mobile) ---------------- */}
// <div className="reviewer-card-container-12ab-reviewerapplications">
//   {processedApplications.map((app) => {
//     const applicantName = app.formData?.["Name"] || "Unknown";
//     const applicantEmail = app.formData?.["Email"] || "N/A";
//     const formId = app.formId || app._id;

//     const date = getSubmissionDate(app);
//     const formatted = date ? date.toLocaleDateString("en-GB") : "N/A";

//     return (
//      <div
//   key={formId}
//   className="reviewer-card-12ab-reviewerapplications"
// >
//   <div className="reviewer-card-header-12ab">
//     <div>
//       {/* <p><strong>Applicant:</strong> {applicantName}</p>
//       <p><strong>Email:</strong> {applicantEmail}</p> */}
//       {/* START highlight applicant (CARD) */}
// <p>
//   <strong>Applicant:</strong>{" "}
//   <span
//     dangerouslySetInnerHTML={{
//       __html: highlightMatch(applicantName, debouncedSearch),
//     }}
//   />
// </p>
// {/* END highlight applicant (CARD) */}
// {/* START highlight email (CARD) */}
// <p>
//   <strong>Email:</strong>{" "}
//   <span
//     dangerouslySetInnerHTML={{
//       __html: highlightMatch(applicantEmail, debouncedSearch),
//     }}
//   />
// </p>
// {/* END highlight email (CARD) */}

//       <p><strong>Submitted On:</strong> {formatted}</p>
//     </div>

// <button
//   className="reviewer-small-btn-12ab-reviewerapplications"
//   onClick={() => {

//     // SAME LOGIC AS TABLE: extract first file
//     const fileObj = app.files?.[0];

//     navigate(`/reviewer/evaluate/${formId}`, {
//       state: {
//         applicantName,
//         applicantEmail,
//         applicantConceptFile: fileObj?.path || "",
//         app,
//       },
//     });
//   }}
// >
//   Evaluate
// </button>

//   </div>
// </div>

//     );
//   })}
// </div>

//       </div>
//     </>
//   );
// };

// export default ReviewerApplications;


///////navbar sidebar correctly work 
// import React, { useEffect, useState } from "react";
// import { Link, useNavigate, useParams } from "react-router-dom";
// import ENDPOINT_URL from "../../config";
// import NavbarReviewer from "../Reviewer/ReviewerUI/NavbarReviewer";
// import SidebarReviewer from "../Reviewer/ReviewerUI/SidebarReviewer";
// import "./ReviewerApplications.css";
// import { toast } from "react-toastify";

// const ReviewerApplications = () => {
//   const navigate = useNavigate();
//   const { pipelineId } = useParams();

//   const [reviewer, setReviewer] = useState({});
//   const [applications, setApplications] = useState([]);

//   // Date filter + sort
//   const [sortOrder, setSortOrder] = useState("desc");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");

//   // ---------------- FETCH REVIEWER DETAILS ----------------
//   const fetchReviewerDetails = async () => {
//     try {
//       const res = await fetch(`${ENDPOINT_URL}/api/reviewers/me`, {
//         credentials: "include",
//       });

//       if (!res.ok) {
//         const err = await res.json().catch(() => null);
//         throw new Error(err?.error || "Failed to load reviewer details");
//       }

//       const data = await res.json();
//       setReviewer(data);
//     } catch (err) {
//       toast.error(err.message);
//     }
//   };

//   // ---------------- FETCH ASSIGNED APPLICATIONS ----------------
//   const fetchAssignedForms = async () => {
//     try {
//       const token = localStorage.getItem("reviewer_token");

//       const res = await fetch(`${ENDPOINT_URL}/api/reviewer/forms`, {
//         method: "GET",
//         credentials: "include",
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "application/json",
//         },
//       });

//       if (!res.ok) {
//         const err = await res.json().catch(() => null);
//         throw new Error(err?.message || "Unable to load applications");
//       }

//       const data = await res.json();

//       const allForms = Array.isArray(data) ? data : data.forms || [];

//       const filtered = pipelineId
//         ? allForms.filter((f) => f.pipelineId === pipelineId)
//         : allForms;

//       setApplications(filtered);
//     } catch (err) {
//       toast.error(err.message);
//     }
//   };

//   useEffect(() => {
//     fetchReviewerDetails();
//     fetchAssignedForms();
//   }, [pipelineId]);

//   // ---------------- DATE & SORT LOGIC ----------------
//   const getSubmissionDate = (app) => {
//     const d = new Date(app.formSubmissionTime || app.createdAt);
//     return isNaN(d) ? null : d;
//   };

//   const filterByDate = (app) => {
//     const d = getSubmissionDate(app);
//     if (!d) return false;

//     let ok = true;
//     if (startDate) ok = ok && d >= new Date(startDate);
//     if (endDate) ok = ok && d <= new Date(endDate);
//     return ok;
//   };

//   const sortByDate = (a, b) => {
//     const da = getSubmissionDate(a);
//     const db = getSubmissionDate(b);
//     if (!da || !db) return 0;
//     return sortOrder === "asc" ? da - db : db - da;
//   };

//   const processedApplications = [...applications]
//     .filter(filterByDate)
//     .sort(sortByDate);

//   return (
//     <div className="reviewer-layout-wrapper-12ab-reviewerapplications">
//       {/* NAVBAR */}
//       <NavbarReviewer reviewer={reviewer} />

//       <div className="reviewer-main-section-12ab-reviewerapplications">
//         {/* SIDEBAR */}
//         <SidebarReviewer />

//         {/* CONTENT */}
//         <div className="reviewer-content-area-12ab-reviewerapplications">
//           <h2 className="reviewer-app-title-12ab-reviewerapplications">
//             Assigned Applications
//           </h2>

//           {/* ---------------- FILTER BAR ---------------- */}
//           <div className="reviewer-filter-bar-12ab-reviewerapplications">
//             <input
//               type="date"
//               className="reviewer-filter-input-12ab-reviewerapplications"
//               value={startDate}
//               onChange={(e) => setStartDate(e.target.value)}
//             />

//             <input
//               type="date"
//               className="reviewer-filter-input-12ab-reviewerapplications"
//               value={endDate}
//               onChange={(e) => setEndDate(e.target.value)}
//             />

//             <select
//               className="reviewer-filter-input-12ab-reviewerapplications"
//               value={sortOrder}
//               onChange={(e) => setSortOrder(e.target.value)}
//             >
//               <option value="desc">Newest First</option>
//               <option value="asc">Oldest First</option>
//             </select>
//           </div>

//           {/* ---------------- RESPONSIVE VIEW ---------------- */}
//           <div className="reviewer-responsive-container-12ab-reviewerapplications">

//             {/* ================= MOBILE & TABLET CARDS ================= */}
//             <div className="reviewer-card-container-12ab-reviewerapplications">
//               {processedApplications.map((app) => (
//                 <div
//                   key={app._id}
//                   className="reviewer-card-12ab-reviewerapplications"
//                 >
//                   <p><strong>Name:</strong> {app.formData?.Name}</p>
//                   <p><strong>Email:</strong> {app.formData?.Email}</p>
//                   <p>
//                     <strong>Submitted:</strong>{" "}
//                     {new Date(app.formSubmissionTime).toLocaleDateString()}
//                   </p>

//                   <button
//                     className="reviewer-card-btn-12ab-reviewerapplications"
//                     onClick={() =>
//                       navigate(`/reviewer/evaluate/${app._id}`, { state: app })
//                     }
//                   >
//                     Review Application
//                   </button>
//                 </div>
//               ))}
//             </div>

//             {/* ================= LAPTOP / DESKTOP TABLE ================= */}
//             <table className="reviewer-table-12ab-reviewerapplications">
//               <thead>
//                 <tr>
//                   <th>Name</th>
//                   <th>Email</th>
//                   <th>Submitted On</th>
//                   <th>Action</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {processedApplications.map((app) => (
//                   <tr key={app._id}>
//                     <td>{app.formData?.Name}</td>
//                     <td>{app.formData?.Email}</td>
//                     <td>
//                       {new Date(app.formSubmissionTime).toLocaleDateString()}
//                     </td>

//                     <td>
//                       <button
//                         className="reviewer-table-btn-12ab-reviewerapplications"
//                         onClick={() =>
//                           navigate(`/reviewer/evaluate/${app._id}`, {
//                             state: app,
//                           })
//                         }
//                       >
//                         Review
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ReviewerApplications;




//////////////before responsive 
// import React, { useEffect, useState } from "react";
// import { Link, useNavigate, useParams } from "react-router-dom";
// import ENDPOINT_URL from "../../config";
// import { IoHomeOutline } from "react-icons/io5";
// import { IoIosLink } from "react-icons/io";
// import { IoCheckmarkDoneCircleOutline } from "react-icons/io5";
// import "./ReviewerApplications.css";
// import { toast } from "react-toastify";

// const ReviewerApplications = () => {
//   const navigate = useNavigate();
//   const { pipelineId } = useParams();

//   const [reviewer, setReviewer] = useState({});
//   const [applications, setApplications] = useState([]);

//   // 👉 NEW: date filter & sort state (for "Submitted On")
//   const [sortOrder, setSortOrder] = useState("desc"); // 'asc' | 'desc'
//   const [startDate, setStartDate] = useState(""); // "YYYY-MM-DD"
//   const [endDate, setEndDate] = useState(""); // "YYYY-MM-DD"

//   // ---------------- FETCH REVIEWER INFO ----------------
//   const fetchReviewerDetails = async () => {
//     console.log("API Request: GET /api/reviewers/me");

//     try {
//       const res = await fetch(`${ENDPOINT_URL}/api/reviewers/me`, {
//         credentials: "include",
//       });

//       if (!res.ok) {
//         const errData = await res.json().catch(() => null);
//         console.error("API Error Response (Reviewer):", errData);
//         throw new Error(errData?.error || "Failed to load reviewer details");
//       }

//       const data = await res.json();
//       console.log("API Response Data (Reviewer):", data);

//       setReviewer(data);
//     } catch (err) {
//       console.error("API Error (Reviewer):", err);
//       toast.error(err?.message || "Failed to load reviewer details.");
//     }
//   };

//   // ---------------- FETCH ASSIGNED FORMS ----------------
//   const fetchAssignedForms = async () => {
//     console.log("API Request: GET /api/reviewer/forms");

//     try {
//       const token = localStorage.getItem("reviewer_token");
//       const res = await fetch(`${ENDPOINT_URL}/api/reviewer/forms`, {
//         method: "GET",
//         credentials: "include",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       if (!res.ok) {
//         const errData = await res.json().catch(() => null);
//         console.error("API Error Response (Forms):", errData);
//         throw new Error(errData?.message || "Failed to load assigned forms");
//       }

//       const data = await res.json();
//       console.log("API Response Data (Assigned Forms):", data);

//       const allForms = Array.isArray(data) ? data : data.forms || [];

//       const filtered = pipelineId
//         ? allForms.filter((f) => f.pipelineId === pipelineId)
//         : allForms;

//       setApplications(filtered);
//     } catch (err) {
//       console.error("API Error (Forms):", err);
//       toast.error(err?.message || "Failed to load assigned forms.");
//     }
//   };

//   useEffect(() => {
//     fetchReviewerDetails();
//     fetchAssignedForms();
//   }, [pipelineId]);

//   // ---------------- LOGOUT ----------------
//   const handleLogout = () => {
//     console.log("Reviewer logged out");

//     localStorage.removeItem("reviewer_token");
//     localStorage.removeItem("reviewer_info");

//     window.location.href = "/login";
//   };

//   // ---------------- DATE UTIL FUNCTIONS ----------------
//   const getSubmissionDate = (app) => {
//     const dateValue = app.formSubmissionTime || app.createdAt;
//     if (!dateValue) return null;
//     const d = new Date(dateValue);
//     return isNaN(d.getTime()) ? null : d;
//   };

//   const filterByDateRange = (app) => {
//     const d = getSubmissionDate(app);
//     if (!d) return false;

//     // No filters applied
//     if (!startDate && !endDate) return true;

//     const onlyDate = (date) =>
//       new Date(date.getFullYear(), date.getMonth(), date.getDate());

//     let ok = true;

//     if (startDate) {
//       const from = new Date(startDate); // 00:00 of start date
//       ok = ok && onlyDate(d) >= onlyDate(from);
//     }

//     if (endDate) {
//       const to = new Date(endDate); // 00:00 of end date
//       ok = ok && onlyDate(d) <= onlyDate(to);
//     }

//     return ok;
//   };

//   const sortBySubmittedOn = (a, b) => {
//     const da = getSubmissionDate(a);
//     const db = getSubmissionDate(b);

//     if (!da && !db) return 0;
//     if (!da) return 1;
//     if (!db) return -1;

//     if (sortOrder === "asc") {
//       // Oldest first
//       return da - db;
//     } else {
//       // Newest first
//       return db - da;
//     }
//   };

//   // 👉 Final processed list for table
//   const processedApplications = [...applications]
//     .filter(filterByDateRange)
//     .sort(sortBySubmittedOn);

//   const presentationLabel =
//     "Upload the Presentation (as per the format attached in the description above)";

//   // const fileObj = app.files?.find((f) =>
//   //   f.labelName?.includes(presentationLabel)
//   // );
//   return (
//     <div className="dashboard-reviewerapplications">
//       {/* ================ SIDEBAR ================ */}
//       <aside className="sidebar-reviewerapplications">
//         <div className="logo-container-reviewerapplications">
//           <img
//             src="/navbar/drishtilogo.jpg"
//             alt="Logo"
//             className="dristilogo-reviewerapplications"
//           />
//         </div>

//         <div className="nav-container-reviewerapplications">
//           <nav className="nav-reviewerapplications">
//             <ul>
//               <li>
//                 <Link to="/reviewer/pipelines/applicant">
//                   <IoIosLink className="nav-icon-reviewerapplications" />
//                   Assigned Application
//                 </Link>
//               </li>
//               <li>
//                 <Link to="/reviewer/summary">
//                   <IoCheckmarkDoneCircleOutline className="nav-icon-reviewerdashboard" />
//                   Reviewed Application
//                 </Link>
//               </li>
//               {/* <li>
//                 <Link to="/reviewer/summary">
//                   <IoCheckmarkDoneCircleOutline className="nav-icon-reviewerapplications" />
//                   Completed Evaluations
//                 </Link>
//               </li> */}
//             </ul>
//           </nav>
//         </div>
//       </aside>

//       {/* ================ MAIN CONTENT ================ */}
//       <main className="main-content-reviewerapplications">
//         {/* HEADER */}
//         <header className="header-reviewerapplications">
//           <span className="founder-reviewerapplications">
//             Pipeline – Applications
//           </span>

//           <div className="profile-section-reviewerapplications">
//             <div className="user-info-reviewerapplications">
//               <span className="user-initials-reviewerapplications">
//                 <img src="/navbar/login.png" alt="User" width="40" />
//               </span>

//               <div>
//                 <span className="user-name-reviewerapplications">
//                   {reviewer.username}
//                 </span>
//                 <br />
//                 <span className="user-email-reviewerapplications">
//                   {reviewer.email}
//                 </span>
//               </div>
//             </div>

//             <button
//               className="logout-button-reviewerapplications"
//               onClick={handleLogout}
//             >
//               Logout
//             </button>
//           </div>
//         </header>

//         {/* ================ APPLICATIONS TABLE ================ */}
//         <section className="content-reviewerapplications">
//           <div className="table-box-reviewerapplications">
//             <div
//               style={{
//                 display: "flex",
//                 justifyContent: "space-between",
//                 alignItems: "center",
//                 gap: "1rem",
//                 marginBottom: "10px",
//                 flexWrap: "wrap",
//               }}
//             >
//               <h3 className="subheading-reviewerapplications">
//                 Applications in Pipeline
//               </h3>

//               {/* 👉 Date filter + sort controls */}

//               {/* <button
//                 className="backbtn-reviewerapplications"
//                 onClick={() => navigate("/reviewer/pipelines")}
//               >
//                 ← Back
//               </button> */}
//             </div>
//             <div
//               style={{
//                 display: "flex",
//                 gap: "10px",
//                 alignItems: "center",
//                 flexWrap: "wrap",
//               }}
//             >
//               <div>
//                 <label style={{ fontSize: "0.85rem", marginRight: "4px" }}>
//                   From:
//                 </label>
//                 <input
//                   type="date"
//                   value={startDate}
//                   onChange={(e) => setStartDate(e.target.value)}
//                   style={{ padding: "4px 6px" }}
//                 />
//               </div>

//               <div>
//                 <label style={{ fontSize: "0.85rem", marginRight: "4px" }}>
//                   To:
//                 </label>
//                 <input
//                   type="date"
//                   value={endDate}
//                   onChange={(e) => setEndDate(e.target.value)}
//                   style={{ padding: "4px 6px" }}
//                 />
//               </div>

//               <div>
//                 <label style={{ fontSize: "0.85rem", marginRight: "4px" }}>
//                   Sort:
//                 </label>
//                 <select
//                   value={sortOrder}
//                   onChange={(e) => setSortOrder(e.target.value)}
//                   style={{ padding: "4px 6px" }}
//                 >
//                   <option value="desc">Newest first</option>
//                   <option value="asc">Oldest first</option>
//                 </select>
//               </div>
//             </div>

//             <table className="table-reviewerapplications">
//               <thead>
//                 <tr>
//                   <th>Applicant</th>
//                   <th>Email</th>
//                   <th>
//                     Submitted On
//                     {/* Optional: show small hint it's sortable by controls above */}
//                   </th>
//                   <th>Action</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {processedApplications.length > 0 ? (
//                   processedApplications.map((app) => {
//                     const submittedDate = getSubmissionDate(app);
//                     return (
//                       <tr key={app._id}>
//                         <td>{app.formData?.["Name"] || "-"}</td>
//                         <td>{app.formData?.["Email"] || "-"}</td>
//                         <td>
//                           {submittedDate
//                             ? submittedDate.toLocaleDateString()
//                             : "-"}
//                         </td>
//                         <td>
//                           <button
//                             className="btn-reviewerapplications"
//                             onClick={() => {
//                               const fileObj = app.files?.find((f) =>
//                                 f.labelName?.some((lbl) =>
//                                   lbl
//                                     .toLowerCase()
//                                     .includes("upload the presentation")
//                                 )
//                               );

//                               navigate(`/reviewer/evaluate/${app._id}`, {
//                                 state: {
//                                   applicantName: app.formData?.["Name"] || "",
//                                   applicantEmail: app.formData?.["Email"] || "",
//                                   applicantConceptFile: fileObj?.path || "",
//                                 },
//                               });
//                             }}
//                           >
//                             Review Application
//                           </button>
//                         </td>
//                       </tr>
//                     );
//                   })
//                 ) : (
//                   <tr>
//                     <td colSpan="4" style={{ textAlign: "center" }}>
//                       No applications found
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </section>
//       </main>
//     </div>
//   );
// };

// export default ReviewerApplications;
